import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ActionIcon, Button, Divider, Menu } from '@mantine/core';
import {
  Archive,
  Coin,
  Dots,
  Eye,
  Pencil,
  Search,
  Shirt,
  ShirtSport,
  Trash
} from 'tabler-icons-react';
import dayjs from 'dayjs';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import TableView from '../../common/TableView';
import {
  CURRENCY_TYPE_LIST,
  ECOM_PRODUCT_PRICING_TYPE_ENUM,
  ECOM_PRODUCT_STATUS_LIST,
  ECOM_STORE_PRODUCT_PRICING_TYPE_LIST,
  VIEW_ACTIONS_ENUM
} from '../../../config/constants';
import { currencyFormat, formatUtcDate } from '../../../helpers/format';
import { triggerNotification } from '../../../helpers/notification';
import HeaderView from '../../common/HeaderView';
import { useModalState, usePaginationFilter } from '../../../helpers/hooks';
import AppMenu from '../../common/AppMenu';
import AppStack from '../../common/AppStack';
import ChangeProductStatusModal from '../products/ChangeProductStatusModal';
import { getEcomStoreProductStatus } from '../../../helpers/vendorHelper';
import StoreProductPricingModal from './StoreProductPricingModal';
import DeleteProductsModal from '../products/DeleteProductsModal';
import ResourceSelectModal from '../../common/ResourceSelectModal';
import PrintDesignProductQuickCreateModal from '../designer/PrintDesignProductQuickCreateModal';

const TABLE_COLUMNS = [
  {
    label: 'Product',
    value: 'name',
    sortable: true
  },
  // {
  //   label: 'Net purchased',
  //   value: 'net_purchased',
  //   sortable: true
  // },
  // {
  //   label: 'Fundraising goal',
  //   value: 'fundraising_goal',
  //   sortable: true
  // },
  {
    label: 'Status',
    value: 'status',
    sortable: true
  },
  {
    label: 'Vendor price',
    value: 'vendor_price',
    sortable: true
  },
  {
    label: 'Margin',
    value: 'pricing',
    sortable: true
  },
  {
    label: 'Base Price',
    value: 'store_price',
    sortable: true
  },
  {
    label: 'Fees',
    value: 'fees',
    sortable: true
  },
  {
    label: 'Rounding',
    value: 'rounding',
    sortable: true
  },
  {
    label: 'Store price',
    value: 'webstore_price',
    sortable: true
  },
  {
    label: 'Profit',
    value: 'profit',
    sortable: false
  },
  {
    label: 'Taxes',
    value: 'taxes',
    sortable: false
  },
  {
    label: 'Added',
    value: 'created',
    sortable: true
  }
];

const VIEW_ACTIONS = [
  {
    label: 'View',
    value: VIEW_ACTIONS_ENUM.VIEW,
    section: 1,
    icon: <Search size={18} />
  },
  {
    label: 'Change status',
    value: VIEW_ACTIONS_ENUM.CHANGE_STATUS,
    section: 1,
    icon: <Eye size={18} />
  },
  {
    label: 'Archive product',
    value: VIEW_ACTIONS_ENUM.ARCHIVE,
    icon: <Archive size={18} />,
    section: 2
  },
  {
    label: 'Delete',
    value: VIEW_ACTIONS_ENUM.DELETE,
    icon: <Trash color="red" size={18} />,
    section: 2,
    color: 'red'
  }
];

const StoreProductTable = () => {
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const { state: authState } = useContext(AuthContext);
  const { state, fetchPaginatedStoreProducts } = useContext(StoreContext);
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const [selectedRows, setSelectedRows] = useState([]);
  const {
    pageIndex,
    pageSize,
    totalCount,
    totalPages,
    totalNoFilterCount,
    data: productData,
    filter,
    loading: dataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh
  } = usePaginationFilter(
    {
      search: '',
      status: ''
    },
    (newFilter) => {
      setSelectedRows([]);
      fetchPaginatedStoreProducts(
        authState.pkEcomStore,
        newFilter,
        null,
        (error) => {
          triggerNotification(error);
        }
      );
    },
    state.paginatedEcomStoreProducts
  );
  const loading = !hasFetched.current || dataLoading;
  const noProductsCreated = !loading && totalNoFilterCount === 0;
  const selectedData = productData.filter((f) =>
    selectedRows.includes(f.pkEcomStoreProduct)
  );

  const selectedStore = authState.ecomStores.find(
    (v) => v.pkEcomStore.toString() === authState.pkEcomStore?.toString()
  );
  const defaultCurrency = CURRENCY_TYPE_LIST.find(
    (c) => c.value.toString() === selectedStore?.fkCurrencyType?.toString()
  );

  const productFilter = filter
    ? {
        search: filter.search,
        fkEcomProductStatus: filter.fkEcomProductStatus,
        fkEcomPricingType: filter.fkEcomProductType,
        fkEcomStoreCollection: filter.fkEcomStoreCollection
      }
    : null;

  useEffect(() => {
    if (authState.pkEcomStore) {
      fetchPaginatedStoreProducts(authState.pkEcomStore, {}, null, (error) => {
        triggerNotification(error);
      });
      hasFetched.current = true;
    }
  }, [authState.pkEcomStore]);

  return (
    <HeaderView
      breadcrumbs={[
        {
          title: 'Products',
          to: '/merchant/products'
        }
      ]}
      rightSection={
        !loading && (
          <>
            {/* {!noProductsCreated && (
              <Button
                color="dark"
                onClick={() => onOpenModal(VIEW_ACTIONS_ENUM.EXPORT)}
                radius="md"
                size="compact-md"
                style={{ fontSize: 14 }}
                variant="outline"
                visibleFrom="xsm"
              >
                Export
              </Button>
            )}

            <Button
              color="dark"
              onClick={() => onOpenModal(VIEW_ACTIONS_ENUM.IMPORT)}
              radius="md"
              size="compact-md"
              style={{ fontSize: 14 }}
              variant="outline"
              visibleFrom="xsm"
            >
              Import
            </Button> */}
            {authState.userData.isAdmin && (
              <Button
                color="dark"
                leftSection={<ShirtSport size={18} />}
                onClick={() => onOpenModal('design_products')}
                radius="md"
                size="compact-md"
                style={{ fontSize: 14 }}
                variant="outline"
              >
                Design products
              </Button>
            )}

            <Button
              color="dark"
              leftSection={<Search size={18} />}
              onClick={() => navigate('/merchant/products/discover')}
              radius="md"
              size="compact-md"
              style={{ fontSize: 14 }}
            >
              Find products
            </Button>
          </>
        )
      }
    >
      <TableView
        columns={TABLE_COLUMNS}
        disabled={noProductsCreated}
        disabledContent={{
          title: 'Find products',
          description: 'Start by adding a product to your store.',
          icon: <Shirt color="#000" size={125} />,
          primaryButton: {
            label: 'Find product',
            icon: <Search size={18} />,
            link: '/merchant/products/discover'
          }
        }}
        emptyMessage="No products found."
        enableInputsWhenLoading
        filters={[
          {
            key: 1,
            label: 'Search',
            placeholder: 'Filter by product',
            value: filter.search,
            onChange: (value) => onFilterChange({ ...filter, search: value })
          },
          {
            key: 2,
            label: 'Collection',
            type: 'select',
            value: filter.fkEcomStoreCollection ?? null,
            placeholder: 'Filter by collection',
            searchable: true,
            clearable: true,
            data: [
              {
                value: '-1',
                label: '-- No Collection --'
              },
              ...state.ecomStoreCollections.value
                .map((c) => ({
                  value: c.pkEcomStoreCollection.toString(),
                  label: c.name
                }))
                .sort((a, b) => a.label.localeCompare(b.label))
            ],
            onChange: (value) =>
              onFilterChange({ ...filter, fkEcomStoreCollection: value }, true)
          },
          {
            key: 3,
            label: 'Pricing type',
            type: 'select',
            value: filter.fkEcomPricingType ?? null,
            placeholder: 'Filter by pricing',
            searchable: true,
            clearable: true,
            data: ECOM_STORE_PRODUCT_PRICING_TYPE_LIST,
            onChange: (value) =>
              onFilterChange({ ...filter, fkEcomPricingType: value }, true)
          },
          {
            key: 4,
            label: 'Status',
            type: 'select',
            value: filter.fkEcomProductStatus ?? null,
            placeholder: 'Filter by status',
            searchable: true,
            clearable: true,
            data: [
              ...ECOM_PRODUCT_STATUS_LIST,
              {
                label: 'Not available',
                value: '-1'
              }
            ],
            onChange: (status) =>
              onFilterChange({ ...filter, fkEcomProductStatus: status }, true)
          }
        ]}
        isDescendingSort={isDescendingSort}
        isLoading={!hasFetched.current || loading}
        noSort
        onAction={(action, item) => {
          const product = productData.find(
            (s) => s.pkEcomStoreProduct === item.key
          );
          if (product) {
            switch (action) {
              case VIEW_ACTIONS_ENUM.VIEW:
                navigate(`/merchant/products/${product.uuid}`);
                break;
              case VIEW_ACTIONS_ENUM.CHANGE_STATUS:
              case VIEW_ACTIONS_ENUM.ARCHIVE:
              case VIEW_ACTIONS_ENUM.DELETE:
              default:
                onOpenModal(action, [product]);
                break;
            }
          }
        }}
        onChangeSortBy={onSortChange}
        onPageChange={onPageChange}
        onRefresh={onRefresh}
        onSelect={setSelectedRows}
        pageIndex={pageIndex}
        rows={productData.map((a) => {
          const pricingPrefix =
            a.fkEcomPricingTypes.length === 1 &&
            a.fkEcomPricingTypes[0]?.toString() ===
              ECOM_PRODUCT_PRICING_TYPE_ENUM.FLAT_ADJUSTMENT.toString()
              ? '$'
              : '';
          const pricingSuffix = a.fkEcomPricingTypes.some(
            (fk) =>
              fk.toString() ===
              ECOM_PRODUCT_PRICING_TYPE_ENUM.PERCENTAGE_ADJUSTMENT.toString()
          )
            ? '%'
            : '';
          const pricingAmountString = `${
            a.minPricingAmount === a.maxPricingAmount ? '' : 'From '
          }+${
            pricingPrefix && !pricingSuffix ? pricingPrefix : ''
          }${currencyFormat(a.minPricingAmount / 100).replace(
            '$',
            ''
          )}${pricingSuffix}`;

          const productCurrency = a.ecomVendorProduct.fkCurrencyTypeOverride
            ? CURRENCY_TYPE_LIST.find(
                (c) =>
                  c.value.toString() ===
                  a.ecomVendorProduct.fkCurrencyTypeOverride.toString()
              )
            : defaultCurrency;

          return {
            key: a.pkEcomStoreProduct,
            actions: VIEW_ACTIONS,
            columns: [
              {
                key: 1,
                uuid: a.uuid,
                label: a.ecomVendorProduct.name,
                component: Link,
                to: `/merchant/products/${a.uuid}`,
                weight: 500,
                subLabel: a.ecomVendorProduct.vendorName,
                src: a.ecomVendorProduct.previewImage ?? null,
                showImage: true
              },
              {
                key: 2,
                ...getEcomStoreProductStatus(a)
              },
              {
                key: 3,
                label:
                  a.minVendorPrice === a.maxVendorPrice
                    ? currencyFormat(a.minVendorPrice / 100)
                    : `${currencyFormat(
                        a.minVendorPrice / 100
                      )} - ${currencyFormat(a.maxVendorPrice / 100)}`
              },
              {
                key: 4,
                label: pricingAmountString
              },
              {
                key: 5,
                label:
                  a.minStorePrice === a.maxStorePrice
                    ? currencyFormat(a.minStorePrice / 100)
                    : `${currencyFormat(
                        a.minStorePrice / 100
                      )} - ${currencyFormat(a.maxStorePrice / 100)}`
              },
              {
                key: 6,
                label:
                  a.minServiceFee === a.maxServiceFee
                    ? currencyFormat(a.minServiceFee / 100)
                    : `${currencyFormat(
                        a.minServiceFee / 100
                      )} - ${currencyFormat(a.maxServiceFee / 100)}`
              },
              {
                key: 7,
                label:
                  a.minRoundingAdjustment === a.maxRoundingAdjustment
                    ? currencyFormat(a.minRoundingAdjustment / 100)
                    : `${currencyFormat(
                        a.minRoundingAdjustment / 100
                      )} - ${currencyFormat(a.maxRoundingAdjustment / 100)}`
              },
              {
                key: 8,
                label:
                  a.minWebstorePrice === a.maxWebstorePrice
                    ? currencyFormat(a.minWebstorePrice / 100)
                    : `${currencyFormat(
                        a.minWebstorePrice / 100
                      )} - ${currencyFormat(a.maxWebstorePrice / 100)}`
              },
              {
                key: 9,
                label:
                  a.minStoreProfit === a.maxStoreProfit
                    ? currencyFormat(a.minStoreProfit / 100)
                    : `${currencyFormat(
                        a.minStoreProfit / 100
                      )} - ${currencyFormat(a.maxStoreProfit / 100)}`
              },
              {
                key: 10,
                label: a.isTaxExempt
                  ? 'Tax exempt'
                  : `${a.ecomStoreTax.reduce((r, c) => r + c.taxRate, 0)}%`
              },
              {
                key: 11,
                label: dayjs(formatUtcDate(a.createdAt)).format('MMMM D, YYYY')
              }
            ]
          };
        })}
        selectActionSection={
          <AppMenu
            control={
              <ActionIcon color="#ced4da" size="lg" variant="outline">
                <Dots color="#000" />
              </ActionIcon>
            }
            styles={{ dropdown: { padding: 0 } }}
          >
            <AppStack style={{ padding: 2, gap: 1 }}>
              <Menu.Item
                leftSection={<Pencil size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (
                    selectedData.length === totalCount &&
                    !filter.search &&
                    !filter.fkEcomProductStatus &&
                    !filter.fkEcomPricingType &&
                    !filter.fkEcomStoreCollection
                  ) {
                    navigate(`/merchant/products/bulk-edit`);
                  }
                  else {
                    onOpenModal('bulk_edit', [...selectedData]);
                  }
                }}
                visibleFrom="xsm"
              >
                Bulk edit
              </Menu.Item>
              <Menu.Item
                leftSection={<Coin size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal('set_pricing', [...selectedData]);
                }}
              >
                Set pricing
              </Menu.Item>
              <Menu.Item
                leftSection={<Eye size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal(VIEW_ACTIONS_ENUM.CHANGE_STATUS, [
                    ...selectedData
                  ]);
                }}
              >
                Change status
              </Menu.Item>
            </AppStack>
            <Divider />
            <AppStack style={{ padding: 2, gap: 1 }}>
              <Menu.Item
                leftSection={<Archive size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal(VIEW_ACTIONS_ENUM.ARCHIVE, [...selectedData]);
                }}
              >
                Archive products
              </Menu.Item>
              <Menu.Item
                leftSection={<Trash size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal(VIEW_ACTIONS_ENUM.DELETE, [...selectedData]);
                }}
                style={{ color: 'red' }}
              >
                Delete products
              </Menu.Item>
            </AppStack>
          </AppMenu>
        }
        selectedRows={selectedRows}
        sortBy={filter.sort || TABLE_COLUMNS[0].value}
        tableTitle="Store Products"
        totalCount={totalCount}
        totalPages={totalPages}
      />

      <ResourceSelectModal
        filter={productFilter}
        isOpen={modalState.isOpen && modalState.action === 'bulk_edit'}
        onClose={onCloseModal}
        onSelect={(selectType) => {
          if (selectType === 'all') {
            const params = new URLSearchParams();
            if (filter.search) {
              params.append(`search`, filter.search);
            }

            if (filter.fkEcomProductStatus) {
              params.append(`status`, filter.fkEcomProductStatus);
            }

            if (filter.fkEcomPricingType) {
              params.append(`pricingType`, filter.fkEcomPricingType);
            }

            if (filter.fkEcomStoreCollection) {
              params.append(`collection`, filter.fkEcomStoreCollection);
            }

            const queryString = params.toString();
            navigate(
              `/merchant/products/bulk-edit${
                queryString ? `?${queryString}` : ''
              }`
            );
          }
          else {
            navigate(
              `/merchant/products/bulk-edit?ids=${selectedData
                .map((f) => f.uuid)
                .join(',')}`
            );
          }
        }}
        selectedIds={selectedData.map((f) => f.uuid)}
        selectLabel="Edit for"
        selectLabelDescription="Select the products you want to edit."
        size={450}
        title="Bulk edit products?"
        totalItemCount={totalCount}
      />

      <ChangeProductStatusModal
        archiveProducts={modalState.action === VIEW_ACTIONS_ENUM.ARCHIVE}
        filter={productFilter}
        fkEcomStore={authState.pkEcomStore}
        isOpen={
          modalState.isOpen &&
          (modalState.action === VIEW_ACTIONS_ENUM.CHANGE_STATUS ||
            modalState.action === VIEW_ACTIONS_ENUM.ARCHIVE)
        }
        onClose={onCloseModal}
        onSuccess={() => {
          onRefresh();
          setSelectedRows([]);
        }}
        selectedProductIds={selectedData.map((f) => f.uuid)}
        totalItemCount={totalCount}
      />

      <StoreProductPricingModal
        filter={productFilter}
        isOpen={modalState.isOpen && modalState.action === 'set_pricing'}
        onClose={onCloseModal}
        onSuccess={() => {
          onRefresh();
          setSelectedRows([]);
        }}
        selectedProductIds={selectedData.map((f) => f.uuid)}
        totalItemCount={totalCount}
      />

      <DeleteProductsModal
        filter={productFilter}
        fkEcomStore={authState.pkEcomStore}
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
        }
        onClose={onCloseModal}
        onSuccess={() => {
          onRefresh();
          setSelectedRows([]);
        }}
        products={modalState.item || []}
        totalItemCount={modalState.item?.length || 0}
      />

      <PrintDesignProductQuickCreateModal
        fkEcomStore={authState.pkEcomStore}
        isOpen={modalState.isOpen && modalState.action === 'design_products'}
        onClose={onCloseModal}
        onComplete={(designProducts) => {
          navigate(
            `/merchant/products/bulk-edit?ids=${designProducts
              .map((f) => f.uuid)
              .join(',')}`
          );
        }}
      />
    </HeaderView>
  );
};

StoreProductTable.propTypes = {};

export default StoreProductTable;
